<template>
  <Head :title="`ShoutOut: Bestel nu een persoonlijke video van ${vip.first_name} ${vip.last_name}`">
    <meta
      name="description"
      :content="`ShoutOut: Bestel nu een persoonlijke video van ${vip.first_name} ${vip.last_name}`"
    >
    <link
      rel="canonical"
      :href="localeRoute('frontend.vips.show', [vip])"
    >

    <meta
      property="og:type"
      content="website"
    >
    <meta
      property="og:url"
      :content="localeRoute('frontend.vips.show', [vip])"
    >
    <meta
      property="og:title"
      :content="vip.content.display_name"
    >
    <meta
      property="og:description"
      :content="`ShoutOut: Bestel nu een persoonlijke video van ${vip.first_name} ${vip.last_name}`"
    >
    <meta
      v-if="vip.content.poster?.url"
      property="og:image"
      :content="vip.content.poster.url"
    >
    <meta
      name="twitter:card"
      content="summary_large_image"
    >
    <meta
      property="twitter:domain"
      content="shoutout.vip"
    >
    <meta
      property="twitter:url"
      :content="localeRoute('frontend.vips.show', [vip])"
    >
    <meta
      name="twitter:title"
      :content="vip.content.display_name"
    >
    <meta
      name="twitter:description"
      :content="`ShoutOut: Bestel nu een persoonlijke video van ${vip.first_name} ${vip.last_name}`"
    >
    <meta
      v-if="vip.content.poster?.url"
      name="twitter:image"
      :content="vip.content.poster.url"
    >
  </Head>

  <section class="vip-details relative pt-4 pb-8 lg:pb-20">
    <div class="container">
      <h1 class="text-5xl font-bold mt-3 lg:text-7xl text-center block">
        {{ vip.content.display_name }}
      </h1>

      <div class="flex flex-wrap mt-4 md:mt-8">
        <div class="w-full relative z-20 md:w-2/5 lg:w-5/12">
          <template v-if="vip.public_shout_outs.length === 0">
            <div
              v-if="vip.content.video"
              class="rounded-xl overflow-clip"
            >
              <VideoPlayer
                controls
                :plyr="true"
                :autoplay="false"
                :poster="vip.content.poster?.url"
                :src="vip.content.video.url"
                width="524"
                height="524"
              />
            </div>
            <img
              v-else-if="vip.content.image"
              :src="vip.content.image.url"
              :alt="vip.content.display_name"
              width="524"
              height="524"
              class="rounded-xl"
            >
          </template>
          <template v-else>
            <Splide
              ref="mainSlider"
              :has-track="false"
              :options="mainSliderOptions"
              class="vip-slider relative"
              @splide:move="pauseActiveVideo"
            >
              <SplideTrack>
                <SplideSlide>
                  <div
                    v-if="vip.content.video"
                    class="rounded-xl overflow-clip"
                  >
                    <VideoPlayer
                      controls
                      :plyr="true"
                      :autoplay="false"
                      :poster="vip.content.poster?.url"
                      :src="vip.content.video.url"
                      width="524"
                      height="524"
                    />
                  </div>
                  <img
                    v-else-if="vip.content.image"
                    :src="vip.content.image.url"
                    :alt="vip.content.display_name"
                    width="524"
                    height="524"
                    class="rounded-xl"
                  >
                </SplideSlide>

                <SplideSlide
                  v-for="(shout_out, i) in vip.public_shout_outs"
                  :key="`shout-out-${i}`"
                >
                  <div class="rounded-xl overflow-clip">
                    <VideoPlayer
                      controls
                      :plyr="true"
                      :autoplay="false"
                      :poster="shout_out.poster?.url"
                      :src="shout_out.video.url"
                      preload="none"
                      width="524"
                      height="524"
                    />
                  </div>
                </SplideSlide>
              </SplideTrack>
            </Splide>

            <div class="mt-2">
              <Splide
                ref="thumbSlider"
                :has-track="false"
                :options="thumbSliderOptions"
                class="vip-thumbnail-slider"
              >
                <SplideTrack>
                  <SplideSlide>
                    <img
                      :src="vip.content.image.url"
                      :alt="vip.content.display_name"
                      width="92"
                      height="92"
                      class="rounded-xl max-h-[92px]"
                    >
                  </SplideSlide>

                  <SplideSlide
                    v-for="(shout_out, i) in vip.public_shout_outs"
                    :key="`shout-out-${i}`"
                  >
                    <img
                      :src="shout_out.poster?.url"
                      :alt="vip.content.display_name"
                      width="92"
                      height="92"
                      class="rounded-xl max-h-[92px]"
                    >
                  </SplideSlide>
                </SplideTrack>
              </Splide>
            </div>
          </template>
        </div>

        <div class="w-full mt-8 lg:w-7/12 md:w-3/5 md:pl-12 lg:pl-12 md:mt-0">
          <div class="mb-3 space-x-4">
            <span class="text-3xl font-bold mt-2 lg:text-4xl">
              &euro; {{ vip.content.price / 100 }}
            </span>
            <span class="text-gray-300 font-medium mt-2 mb-3">
              {{ __('vip.expected-delivery-time') }}
              <strong
                v-if="vip.content.offers_48h"
                class="ml-2"
              >2 {{ $t('general.day', 2) }}</strong>
              <strong
                v-else
                class="ml-2"
              >{{ vip.average_delivery_time }} {{ $t('general.day', vip.average_delivery_time) }}</strong>
            </span>
          </div>

          <div class="relative">
            <!-- eslint-disable vue/no-v-html -->
            <div
              ref="bio"
              class="border-t border-black text-gray-300 pt-3 font-medium lg:mb-0"
              :class="{'line-clamp-3': !readMore}"
              v-html="vip.content.about"
            />
            <!-- eslint-enable vue/no-v-html -->
            <button
              v-if="readMoreButton && !readMore"
              type="button"
              class="text-red hover:underline"
              @click="readMore = true"
            >
              {{ __('general.read-more') }}
            </button>
          </div>

          <div
            v-if="vip.content.available"
            class="flex flex-wrap lg:mt-1"
          >
            <VipForm
              class="lg:order-4"
              :vip="vip"
              :occasions="occasions"
              :ai="ai"
            />
          </div>
          <div
            v-if="!vip.content.available"
            class="mt-6 text-red font-bold text-xl"
          >
            {{ __('vip.unavailable') }}
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import {Splide, SplideTrack, SplideSlide} from '@splidejs/vue-splide';
import VideoPlayer from '../../Components/VideoPlayer.vue';
import VipForm from '../../Components/Order/VipForm.vue';
import GoogleTagManager from '../../Mixins/GoogleTagManager';

export default {
  components: {
    Splide, SplideTrack, SplideSlide,
    VipForm,
    VideoPlayer,
  },

  mixins: [GoogleTagManager],

  props: {
    vip: {
      type: Object,
      required: true,
    },

    alternativeVips: {
      type: Array,
      required: true,
    },

    occasions: {
      type: Array,
      required: true,
    },

    ai: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      readMore: false,
      readMoreButton: false,

      mainSliderOptions: {
        gap: '0.5rem',
        perPage: 1,
        arrows: false,
        pagination: false,
        rewind: true,
        autoHeight: true,
      },

      thumbSliderOptions: {
        gap: '1rem',
        perPage: 5,
        arrows: false,
        pagination: false,
        rewind: true,
        isNavigation: true,
        updateOnMove: true,
      },
    };
  },

  mounted () {
    this.gtmViewItem(this.vip);

    if (
      this.$refs.bio.offsetHeight < this.$refs.bio.scrollHeight
      || this.$refs.bio.offsetWidth < this.$refs.bio.scrollWidth
    ) {
      this.readMoreButton = true;
    } else {
      this.readMoreButton = false;
    }

    if (this.$refs.mainSlider && this.$refs.thumbSlider) {
      this.$nextTick(() => {
        this.$refs.mainSlider.sync(this.$refs.thumbSlider.splide);
      });
    }
  },

  methods: {
    pauseActiveVideo(splide, _, prevIndex) {
      const activeSlide = splide.Components.Elements.slides[prevIndex];
      const video = activeSlide.querySelector('video');

      if (video) {
        video.pause();
      }
    },
  },
};
</script>
